import { useEffect, useRef, useState, useCallback } from 'react';

interface Estatistica {
  icon: string;
  valor: number;
  sufixo: string;
  prefixo: string;
  label: string;
}

const estatisticas: Estatistica[] = [
  {
    icon: 'ri-heart-pulse-line',
    valor: 12847,
    sufixo: '',
    prefixo: '+',
    label: 'Vidas transformadas',
  },
  {
    icon: 'ri-mail-heart-line',
    valor: 31520,
    sufixo: '',
    prefixo: '+',
    label: 'Cartas escritas',
  },
  {
    icon: 'ri-global-line',
    valor: 48,
    sufixo: '',
    prefixo: '',
    label: 'Países alcançados',
  },
  {
    icon: 'ri-star-smile-line',
    valor: 98,
    sufixo: '%',
    prefixo: '',
    label: 'Taxa de satisfação',
  },
  {
    icon: 'ri-calendar-check-line',
    valor: 4,
    sufixo: '',
    prefixo: '+',
    label: 'Anos de história',
  },
  {
    icon: 'ri-group-line',
    valor: 5200,
    sufixo: '',
    prefixo: '+',
    label: 'Membros na comunidade',
  },
];

const useCountUp = (end: number, duration: number, start: boolean) => {
  const [count, setCount] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!start) return;
    setFinished(false);

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out with overshoot (elastic feel)
      const eased = progress < 1
        ? 1 - Math.pow(1 - progress, 4)
        : 1;

      setCount(Math.floor(eased * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
        setFinished(true);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, start]);

  return { count, finished };
};

const StatCard = ({
  stat,
  index,
  isVisible,
}: {
  stat: Estatistica;
  index: number;
  isVisible: boolean;
}) => {
  const { count, finished } = useCountUp(stat.valor, 1800 + index * 150, isVisible);
  const [bounced, setBounced] = useState(false);

  useEffect(() => {
    if (finished) {
      setBounced(true);
      const timer = setTimeout(() => setBounced(false), 600);
      return () => clearTimeout(timer);
    }
  }, [finished]);

  const formatNumber = useCallback((num: number) => {
    if (num >= 1000) {
      return num.toLocaleString('pt-BR');
    }
    return num.toString();
  }, []);

  return (
    <div
      className={`group relative text-center p-8 rounded-xl border border-gold-500/10 bg-white/[0.02] backdrop-blur-sm hover:border-gold-500/25 hover:bg-white/[0.04] transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Glow pulse on finish */}
      <div
        className={`absolute inset-0 rounded-xl transition-opacity duration-700 pointer-events-none ${
          bounced ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.08) 0%, transparent 70%)',
        }}
      />

      {/* Icon */}
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-500/15 to-gold-600/5 flex items-center justify-center mx-auto mb-6 group-hover:from-gold-500/25 group-hover:to-gold-600/10 transition-all duration-500 ${
        bounced ? 'scale-110' : 'scale-100'
      }`}
        style={{ transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        <i
          className={`${stat.icon} text-3xl text-gold-500 group-hover:text-gold-400 transition-colors duration-300`}
        />
      </div>

      {/* Number with bounce */}
      <div
        className={`font-['Playfair_Display'] text-4xl lg:text-5xl text-white font-light mb-3 group-hover:text-gold-300 transition-colors duration-300 ${
          bounced ? 'animate-stat-bounce' : ''
        }`}
        style={{
          transform: bounced ? 'scale(1.12)' : 'scale(1)',
          transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          textShadow: finished ? '0 0 20px rgba(201,168,76,0.15)' : 'none',
        }}
      >
        {stat.prefixo}
        {formatNumber(count)}
        {stat.sufixo}
      </div>

      {/* Label */}
      <p className={`font-['Montserrat'] text-xs text-white/40 tracking-wider uppercase font-medium transition-all duration-500 ${
        finished ? 'text-white/55' : ''
      }`}>
        {stat.label}
      </p>

      {/* Decorative line with animation */}
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-transparent via-gold-500/40 to-transparent rounded-full transition-all duration-700 ${
        finished ? 'w-16 opacity-100' : 'w-0 opacity-0'
      } group-hover:w-20 group-hover:opacity-100`} />

      {/* Sparkle particles on finish */}
      {bounced && (
        <>
          <span className="absolute top-4 right-6 w-1 h-1 rounded-full bg-gold-500/60 animate-ping" style={{ animationDuration: '0.8s' }} />
          <span className="absolute top-8 left-5 w-1 h-1 rounded-full bg-gold-500/40 animate-ping" style={{ animationDuration: '1s', animationDelay: '0.1s' }} />
          <span className="absolute bottom-6 right-8 w-0.5 h-0.5 rounded-full bg-gold-500/50 animate-ping" style={{ animationDuration: '0.9s', animationDelay: '0.2s' }} />
        </>
      )}
    </div>
  );
};

const EstatisticasImpacto = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-luxury-cream py-28 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />
      <div className="absolute top-20 right-0 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-600/60">
              Nosso impacto
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
          </div>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl text-luxury-charcoal font-light mb-5">
            Números que{' '}
            <span className="italic text-gold-600">inspiram</span>
          </h2>
          <p className="font-['Montserrat'] text-sm text-luxury-charcoal/40 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
            Cada número representa uma história real de transformação. Pessoas
            que decidiram reescrever seu futuro e encontraram um novo caminho.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {estatisticas.map((stat, index) => (
            <StatCard
              key={index}
              stat={stat}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Testimonial quote */}
        <div
          className={`mt-16 max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="relative bg-white rounded-2xl border border-gold-500/10 p-10 shadow-[0_8px_40px_rgba(201,168,76,0.04)]">
            <i className="ri-double-quotes-l text-4xl text-gold-500/15 absolute top-6 left-8" />
            <p className="font-['Playfair_Display'] text-lg text-luxury-charcoal/70 italic leading-relaxed mb-6">
              Escrevi minha carta há 2 anos, achando que era apenas um exercício.
              Quando a recebi, chorei ao perceber que havia conquistado tudo o que
              sonhei. A carta me mostrou o quanto eu cresci.
            </p>
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center">
                <span className="font-['Montserrat'] text-xs font-bold text-white">
                  MC
                </span>
              </div>
              <div className="text-left">
                <p className="font-['Montserrat'] text-sm font-semibold text-luxury-charcoal">
                  Marina C.
                </p>
                <p className="font-['Montserrat'] text-[10px] text-luxury-charcoal/40 tracking-wider uppercase">
                  São Paulo, SP
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EstatisticasImpacto;
